import React from 'react';
import { motion } from 'framer-motion';
import {
  MapPin,
  CheckCircle,
  AlertTriangle,
  Users,
  Calendar,
  BarChart3,
  FileText,
  Wrench,
  ArrowUpRight,
} from 'lucide-react';

export const AdminDashboardHero = ({ userName }) => {
  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8 rounded-xl bg-gradient-to-r from-red-600 to-red-700 p-8 text-white shadow-lg"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 text-3xl font-bold">Bonjour, {userName}</h1>
          <p className="text-red-100">
            Bienvenue sur votre tableau de bord d'administration
          </p>
        </div>
        <div className="flex items-center gap-3 rounded-lg bg-white/10 px-4 py-3 backdrop-blur-sm">
          <Calendar className="h-5 w-5" />
          <div className="text-sm">
            <div className="font-semibold capitalize">{today}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const kpiConfig = [
  {
    key: 'sites',
    label: 'Sites actifs',
    icon: MapPin,
    getValue: (s) => s.totalSites,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  {
    key: 'operational',
    label: 'Équipements conformes',
    icon: CheckCircle,
    getValue: (s) => s.operational,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    key: 'maintenance',
    label: 'Maintenance requise',
    icon: AlertTriangle,
    getValue: (s) => s.maintenanceRequired,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  {
    key: 'clients',
    label: 'Clients',
    icon: Users,
    getValue: (s) => s.totalClients,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
];

export const AdminKpiGrid = ({ stats }) => (
  <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
    {kpiConfig.map((kpi, index) => (
      <motion.div
        key={kpi.key}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="rounded-xl bg-white p-6 shadow-md"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{kpi.label}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {kpi.getValue(stats)}
            </p>
          </div>
          <div className={`rounded-lg ${kpi.bgColor} p-3 ${kpi.color}`}>
            <kpi.icon className="h-6 w-6" />
          </div>
        </div>
      </motion.div>
    ))}
  </div>
);

const quickActions = [
  {
    title: 'Nouveau client',
    description: 'Créer une fiche client',
    icon: Users,
    path: '/clients/add',
  },
  {
    title: 'Gérer les sites',
    description: 'Ajouter ou modifier des sites',
    icon: MapPin,
    path: '/admin/sites',
  },
  {
    title: 'Planifier une maintenance',
    description: 'Créer une intervention',
    icon: Wrench,
    path: '/maintenance',
  },
  {
    title: 'Gérer les plans',
    description: 'Téléverser un plan',
    icon: BarChart3,
    path: '/plans',
  },
  {
    title: 'Générer un rapport',
    description: 'Créer un document',
    icon: FileText,
    path: '/reports',
  },
];

export const AdminQuickActions = ({ navigate }) => (
  <div className="rounded-xl bg-white p-6 shadow-md">
    <h2 className="mb-4 text-xl font-bold text-gray-900">Actions rapides</h2>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {quickActions.map((action) => (
        <button
          key={action.path}
          onClick={() => navigate(action.path)}
          className="group flex flex-col items-start rounded-lg border border-gray-200 p-4 text-left transition-all hover:border-red-600 hover:shadow-md"
        >
          <div className="mb-3 flex w-full items-center justify-between">
            <div className="rounded-lg bg-red-50 p-2 text-red-600">
              <action.icon className="h-5 w-5" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-gray-400 transition-colors group-hover:text-red-600" />
          </div>
          <h3 className="font-semibold text-gray-900">{action.title}</h3>
          <p className="mt-1 text-sm text-gray-600">{action.description}</p>
        </button>
      ))}
    </div>
  </div>
);

export const AdminSectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-4 flex items-center gap-3">
    <div className="rounded-lg bg-red-600 p-2 text-white">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
    </div>
  </div>
);
